import React, { useEffect, useRef, useState } from 'react';

// Below this short-edge size there is no room for the toggle chrome (a 100x125
// cart tile). The side stays concealed; a full-size view with the control is
// one click away.
const CHROME_MIN_SHORT_EDGE = 160;

// How much of the frame the frosted panel covers, from the hidden side's edge,
// in percent of the rendered height. Face-first (the bottom concealed) leaves
// the top 55% clear, which keeps the chin on school framing. The panel ends
// in one straight edge: a blur fading across a face reads as a smudge, and a
// clean edge reads as deliberate.
//
// The middle line is clear in the default state, so two screenshots, one per
// state, can be stitched into the whole photo. Accepted: this deters the
// casual one-tap screenshot, and a stitch-resistant middle band would have to
// cover the chin and mouth in both states.
export const CONCEAL_PCT = 45;

// The chrome is laid out inside the panel, so what it has to fit in is
// CONCEAL_PCT of the rendered height — NOT the short edge. Those diverge
// sharply on wide tiles: the 281x188 group tile clears the short-edge gate
// comfortably (188) while giving the chrome only ~85px. At or above this
// height the mark stacks a ringed icon over the label; below it, the icon
// sits inline beside the label, which costs no height. Sized so the stacked
// mark (~126px), centred, still starts in the dark part of the tint
// (PANEL_TINT_STOPS).
const CHROME_COMFORTABLE_MIN_BAND = 180;

// The comfortable label is 18px type on one line (~170px) inside 4px of
// wrapper padding a side. Below this width it would wrap on a narrow-but-tall
// image, so step down to the compact tier's 12px type instead.
const CHROME_COMFORTABLE_MIN_WIDTH = 240;

// `backdrop-filter: blur()` is measured in rendered CSS pixels, so a fixed
// radius would conceal a small thumbnail heavily and a fullscreen zoom barely,
// weakest exactly where the image is largest and most worth protecting.
// Deriving it from the element's own rendered short edge keeps concealment
// consistent across sizes. Heavy on purpose: what shows through should be
// colour, not features.
const BLUR_RADIUS_SHORT_EDGE_RATIO = 0.08;
// Floor so a tiny thumbnail still blurs meaningfully. Also the pre-measurement
// fallback, so there is no first frame where the panel is unblurred.
const BLUR_RADIUS_FLOOR_PX = 12;

// The panel's tint, from its straight edge (0%) to the hidden edge (100%):
// light at the edge, so the photo's colour carries through, darkening toward
// the hidden edge. Load-bearing for the label's contrast too: the label has no
// fill of its own, over a blurred photo whose worst case is a blown-out white
// studio backdrop. White text over this tint over pure white measures 4.61:1
// at alpha 0.58 and 5.30:1 at 0.62, so the 4.5:1 AA threshold for the 11-12px
// semibold text is crossed at about 0.575. The chrome is centred in the
// panel, and its first line starts at least 14% in on every tile that shows
// it (the compact mark is ~52px; the tightest case is a 160px-tall square,
// whose 72px panel starts it 10px in; the ringed mark is ~126px, from a
// 180px panel up). The tint passes 0.58 by then. Recompute if you retune the
// stops, CONCEAL_PCT or the chrome's size.
const PANEL_TINT_STOPS: ReadonlyArray<readonly [number, number]> = [
  [0, 0.28],
  [14, 0.6],
  [100, 0.86],
];
const PANEL_TINT_RGB = '14, 17, 25';

// Where backdrop-filter is unsupported (it has been in every evergreen
// browser since Firefox 103, with Safari still reading the -webkit- name),
// the panel cannot blur, so it falls back to a near-opaque fill: it fails
// closed rather than showing the photo through a tint.
const NO_BLUR_FALLBACK_FILL = `rgba(${PANEL_TINT_RGB}, 0.94)`;

// The toggle is an outline pill with no fill of its own; its text takes its
// contrast from the panel tint behind it (see PANEL_TINT_STOPS). The outline
// carries no contrast budget, so it is free to be this faint.
const TOGGLE_OUTLINE = '1px solid rgba(255, 255, 255, 0.45)';

// The ringed mark the comfortable tier stacks above the label.
const ICON_RING_FILL = 'rgba(255, 255, 255, 0.10)';
const ICON_RING_BORDER = '1px solid rgba(255, 255, 255, 0.18)';

type HiddenHalf = 'top' | 'bottom';
type ChromeTier = 'none' | 'compact' | 'comfortable';

// Two tiers of the same mark, both driven by the single ResizeObserver
// measurement below.
//
// `hitPadding` is invisible (the button paints nothing itself) and exists only
// to hold the tap target at >=32px while the painted pill stays small.
// `toggleGap` is the gap we want to *see* between the label and the pill; the
// button's own margin is derived from it so the two stay in step if either
// padding is retuned.
const CHROME_SCALE = {
  compact: {
    iconSize: 14,
    iconGap: 5,
    ringSize: 0,
    labelFontSize: 12,
    toggleFontSize: 11,
    togglePadding: '3px 11px',
    hitPadding: 6,
    toggleGap: 8,
  },
  comfortable: {
    iconSize: 20,
    iconGap: 10,
    ringSize: 40,
    labelFontSize: 18,
    toggleFontSize: 13,
    togglePadding: '6px 16px',
    hitPadding: 8,
    toggleGap: 12,
  },
} as const;

const round = (value: number, digits: number): number =>
  Number(value.toFixed(digits));

// The panel's tint as a gradient from its straight edge toward the hidden
// edge (PANEL_TINT_STOPS).
export const panelTint = (hiddenHalf: HiddenHalf): string => {
  const direction = hiddenHalf === 'bottom' ? 'to bottom' : 'to top';
  const stops = PANEL_TINT_STOPS.map(
    ([at, alpha]) => `rgba(${PANEL_TINT_RGB}, ${alpha}) ${at}%`,
  );
  return `linear-gradient(${direction}, ${stops.join(', ')})`;
};

const supportsBackdropBlur = (): boolean =>
  typeof CSS !== 'undefined' &&
  typeof CSS.supports === 'function' &&
  (CSS.supports('backdrop-filter', 'blur(1px)') ||
    CSS.supports('-webkit-backdrop-filter', 'blur(1px)'));

// Eye-with-slash. Decorative — the adjacent text already carries the
// meaning — so it stays aria-hidden.
const EyeSlashIcon = ({ size }: { size: number }) => (
  <svg
    aria-hidden="true"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: 'block', flexShrink: 0, opacity: 0.9 }}
  >
    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

// Covers one side of the frame with a frosted panel: a heavy blur of whatever
// is behind it, tinted darker toward the hidden edge, with one straight edge
// facing the clear side. The viewer can swap which side is covered.
//
// The panel blurs the live backdrop (backdrop-filter), not a blurred copy of
// the image. A copy only conceals where it is opaque: over a PNG with
// transparent areas the sharp original showed through the copy's
// see-through pixels (stretched hair, stepped edges), a copy fades out over
// two radii at every edge it is clipped to, and a pose PNG composited over a
// CSS background swatch left the swatch unblurred. The backdrop is exactly
// what the parent sees, whatever it is made of. The cost is a live re-sample
// per paint, which an earlier pass avoided on scrolling grids; it is one
// filtered layer per tile, against the five-plus copies the progressive fade
// needed.
//
// Deterrence only: the unblurred image is still in the DOM.
const SplitBlurOverlay = () => {
  // Face-first: conceal the bottom so a parent can still choose a pose.
  const [hiddenHalf, setHiddenHalf] = useState<HiddenHalf>('bottom');
  const [chromeTier, setChromeTier] = useState<ChromeTier>('none');
  const [blurRadiusPx, setBlurRadiusPx] = useState(BLUR_RADIUS_FLOOR_PX);
  const [canBlur] = useState(supportsBackdropBlur);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return;

    const measure = (width: number, height: number) => {
      const shortEdge = Math.min(width, height);

      // One measurement, two decisions. The gate is the short edge (the pill
      // needs horizontal room too); the density tier is the panel's height —
      // what the chrome actually has to fit inside — qualified by width.
      if (shortEdge < CHROME_MIN_SHORT_EDGE) {
        setChromeTier('none');
      } else if (
        (height * CONCEAL_PCT) / 100 >= CHROME_COMFORTABLE_MIN_BAND &&
        width >= CHROME_COMFORTABLE_MIN_WIDTH
      ) {
        setChromeTier('comfortable');
      } else {
        setChromeTier('compact');
      }

      setBlurRadiusPx(
        round(
          Math.max(
            shortEdge * BLUR_RADIUS_SHORT_EDGE_RATIO,
            BLUR_RADIUS_FLOOR_PX,
          ),
          2,
        ),
      );
    };

    // Mount-time measurement, kept as the initial value (and the only value
    // in environments without ResizeObserver). Insufficient on its own for
    // the zoom modal: rmiz mounts this component before animating the modal
    // image up to full size, so a single measurement here catches the
    // pre-animation box and under-blurs the largest, most detailed render of
    // the photo.
    const box = node.getBoundingClientRect();
    measure(box.width, box.height);

    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      measure(width, height);
    });
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  const other: HiddenHalf = hiddenHalf === 'bottom' ? 'top' : 'bottom';
  const panelEdge = hiddenHalf === 'bottom' ? 'top' : 'bottom';
  const panelPosition =
    hiddenHalf === 'bottom'
      ? { top: `${100 - CONCEAL_PCT}%`, bottom: 0 }
      : { top: 0, bottom: `${100 - CONCEAL_PCT}%` };
  const blur = `blur(${blurRadiusPx}px)`;
  const showsChrome = chromeTier !== 'none';
  const comfortable = chromeTier === 'comfortable';
  const scale = CHROME_SCALE[comfortable ? 'comfortable' : 'compact'];

  return (
    // pointer-events: none — this is layered above the underlying <img> on
    // every call site (dashboard tiles, pose selection, the zoom modal), and
    // those clicks must still reach whatever is underneath. Only the toggle
    // button below opts back into pointer events.
    <div
      ref={rootRef}
      data-testid="split-blur-overlay"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        // Explicit stacking against the base <img> this overlay conceals.
        // Without it the root computes z-index: auto, and a host rule that
        // gives the base image its own z-index (e.g. multiConfigStyles.js's
        // ProductItem `img { z-index: 1 }`) paints that image above this
        // entire tree — the tile renders as a fully unprotected photo.
        // Matches the flat overlay's z-index in ImageProtectionOverlay.tsx.
        zIndex: 2,
      }}
    >
      <div
        data-testid="split-blur-layer"
        data-hidden-half={hiddenHalf}
        // The applied radius, for tests and devtools: jsdom drops
        // backdrop-filter from inline styles.
        data-blur-radius={canBlur ? blurRadiusPx : undefined}
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          ...panelPosition,
          backdropFilter: canBlur ? blur : undefined,
          WebkitBackdropFilter: canBlur ? blur : undefined,
          background: canBlur ? panelTint(hiddenHalf) : NO_BLUR_FALLBACK_FILL,
          // A hairline on the straight edge, so it reads as the edge of a
          // pane of frosted glass rather than a cut in the photo.
          [panelEdge === 'top' ? 'borderTop' : 'borderBottom']:
            '1px solid rgba(255, 255, 255, 0.22)',
          pointerEvents: 'none',
          // Explicit, like every layer here: a host rule that gives it a
          // z-index must not lift it over the label and toggle (2/3).
          zIndex: 0,
        }}
      />

      {/*
        The label and the toggle are ONE flow, centred in the panel; the
        tint is dark enough for the label wherever centring puts it (see
        PANEL_TINT_STOPS).
        Laying them out together makes a label/toggle collision structurally
        impossible at any size rather than something to re-tune per tile.

        This deliberately sits OUTSIDE the aria-hidden panel above: the label
        is the only explanation of why the image is concealed, and hiding it
        would hide it from exactly the users who cannot see that it is.
      */}
      {showsChrome && (
        <div
          data-testid="split-blur-label"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            ...panelPosition,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            textAlign: 'center',
            padding: '0 4px',
            // Above the panel (zIndex 0).
            zIndex: 2,
            // The toggle inside re-enables pointer events for itself; the
            // rest of this box must stay out of the way of clicks meant for
            // the image underneath (pose selection, click-to-unzoom).
            pointerEvents: 'none',
            // `line-height` inherits, and at least one host container
            // (dashboard gallery tiles) deliberately sets `line-height: 0`
            // on an ancestor so it shrink-wraps to the image. Without an
            // explicit value here the text below collapses to zero height.
            lineHeight: 1.3,
          }}
        >
          {comfortable && (
            <div
              data-testid="split-blur-icon-ring"
              style={{
                width: scale.ringSize,
                height: scale.ringSize,
                marginBottom: scale.iconGap,
                borderRadius: '50%',
                background: ICON_RING_FILL,
                border: ICON_RING_BORDER,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <EyeSlashIcon size={scale.iconSize} />
            </div>
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: scale.iconGap,
              fontSize: scale.labelFontSize,
              fontWeight: 600,
              letterSpacing: 0.2,
              // Not a substitute for the contrast budget (a shadow isn't
              // measurable under WCAG) — for a blurred photo with a hard
              // light/dark edge running straight through the text.
              textShadow: '0 1px 3px rgba(0, 0, 0, 0.55)',
            }}
          >
            {!comfortable && <EyeSlashIcon size={scale.iconSize} />}
            Content protected
          </div>
          <button
            type="button"
            data-testid="split-blur-toggle"
            onClick={(event) => {
              event.stopPropagation();
              setHiddenHalf(other);
            }}
            style={{
              // In flow under the label. The derivation keeps the *visible*
              // gap at `toggleGap` even though the button's own padding is
              // invisible.
              marginTop: Math.max(scale.toggleGap - scale.hitPadding, 0),
              // The invisible hit padding below the pill must not count when
              // the group is centred, or the visible mark sits that much high.
              marginBottom: -scale.hitPadding,
              zIndex: 3,
              // inline-flex so the box sizes to exactly padding + the span's
              // content height. A plain inline <button> adds a font-metrics
              // "strut" from its own browser-default font-size, which made a
              // ~53px hit region reaching up into the label.
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: scale.hitPadding,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              // Matches the pill's shape so the native focus ring draws as a
              // halo around it. Deliberately NOT `outline: 'none'`: inline
              // styles can't express :focus-visible, and a keyboard user
              // needs a visible ring.
              borderRadius: 999,
              // `line-height` inherits into `<button>` too (UA stylesheets
              // reset `font-family` on form controls, but not this), so it
              // needs its own explicit value under a `line-height: 0` host.
              lineHeight: 1.3,
              // And the reverse for the font: the UA resets it on form
              // controls, so without this the toggle set in the system font
              // beside a label in the page's.
              fontFamily: 'inherit',
              // The root is pointer-events: none; this is the one
              // interactive element inside it, so it opts back in.
              pointerEvents: 'auto',
            }}
          >
            {/* The <button> is the tap target, this <span> the painted pill,
                so the visible chrome stays small while the tappable box
                stays comfortable on phones. */}
            <span
              style={{
                display: 'inline-block',
                padding: scale.togglePadding,
                borderRadius: 999,
                // See TOGGLE_OUTLINE: no fill of its own.
                background: 'transparent',
                border: TOGGLE_OUTLINE,
                color: '#fff',
                fontSize: scale.toggleFontSize,
                fontWeight: 600,
                letterSpacing: 0.2,
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
                lineHeight: 1.3,
                whiteSpace: 'nowrap',
              }}
            >
              {/* Names the side this press will REVEAL, which is the side
                  currently concealed. */}
              See {hiddenHalf}
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default SplitBlurOverlay;
